import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {Group, GroupResourceOption, RoomPartyMember, RoomPartyMemberOption} from '../../../graphql/groups.datatypes';
import {
    Card,
    CardContent,
    CardHeader,
    Chip,
    Grid,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Theme,
    Typography
} from '@material-ui/core';
import NumberSpinner from '../../BookingTool/NumberSpinner';
import {makeStyles} from '@material-ui/styles';
import SearchBar from '../../../components/SearchBar';
import {useSnackbar} from 'notistack';
import {LoadingOverlay} from '../../../components/LoadingOverlay/LoadingOverlay';
import {gqlGroupRoomPartyOptions} from '../../Groups/getGroup.api';
import createRoomPartyMemberOption from './createRoomPartyMemberOption.api';
import {updateRoomPartyMemberOption, updateRoomPartyMemberOptionBulk} from './updateRoomPartyMemberOption.api';
import deleteRoomPartyMemberOption from './deleteRoomPartyMemberOption.api';
import anyLoading from '../../../utils/anyLoading';
import ArrowRight from '@material-ui/icons/ArrowRight';
import ArrowLeft from '@material-ui/icons/ArrowLeft';
import {ResourceOptionAction} from '../../../graphql/resources.datatypes';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {amber, blue, lime} from '@material-ui/core/colors';
import getGroupByRef from '../../Groups/getGroupByRef.api';
import {checkEditLevel} from '../../../utils/checkEditLevel';
import moment from 'moment';
import getGroupWithQuery from '../../Groups/getGroupWithQuery.api';


const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(3)
    },
    container: {
        marginTop: theme.spacing(3),
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    card: {},
    tableButtonsRow: {
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    chipMandatory: {
        backgroundColor: blue[200],
        marginLeft: theme.spacing(1),
    },
    chipAdminOnly: {
        backgroundColor: amber[200],
        marginLeft: theme.spacing(1),
    },
    chipAdminEdited: {
        backgroundColor: lime[200],
        marginLeft: theme.spacing(1),
    },
}));

export const RoomPartyMemberOptionsEditContent = (props: { group: Group, roomPartyMembers: RoomPartyMember[], isAdmin: boolean, canEdit: boolean, }) => {
    const tableScrollRef = useRef<HTMLDivElement>(null);
    const classes = useStyles();
    const {group, roomPartyMembers, isAdmin, canEdit} = props;
    const [showScrollers, setShowScrollers] = useState(false);
    const [optionsFilter, setOptionsFilter] = useState('');
    const {enqueueSnackbar} = useSnackbar();
    const [sendCreateRoomPartyMemberOption, createRoomPartyMemberOptionData] = createRoomPartyMemberOption();
    const [sendUpdateRoomPartyMemberOption, updateRoomPartyMemberOptionData] = updateRoomPartyMemberOption();
    const [sendUpdateRoomPartyMemberOptionBulk, updateRoomPartyMemberOptionBulkData] = updateRoomPartyMemberOptionBulk();
    const [sendDeleteRoomPartyMemberOption, deleteRoomPartyMemberOptionData] = deleteRoomPartyMemberOption();
    const loadingData = anyLoading(createRoomPartyMemberOptionData, updateRoomPartyMemberOptionData, deleteRoomPartyMemberOptionData, updateRoomPartyMemberOptionBulkData);

    const handleLeftClick = () => {
        if (tableScrollRef.current) tableScrollRef.current.scrollLeft += 120;
    };
    const handleRightClick = () => {
        if (tableScrollRef.current) tableScrollRef.current.scrollLeft -= 120;
    };
    const testShowScrollers = () => {
        if (tableScrollRef && tableScrollRef.current) {
            setShowScrollers(tableScrollRef.current.scrollWidth > tableScrollRef.current.clientWidth);
        }
    }

    useLayoutEffect(() => {
        testShowScrollers();
    }, []);

    let movementTimer: NodeJS.Timeout | null = null;
    window.addEventListener('resize', () => {
        if (movementTimer) clearInterval(movementTimer);
        movementTimer = setTimeout(testShowScrollers, 100);
    });

    const setAllRoomPartyMemberOption = (groupOption: GroupResourceOption, action: 'increment' | 'decrement', maxGuestValue: number) => {

        if (maxGuestValue <= 0 && action === 'increment') {
            enqueueSnackbar('Cannot increment: No capacity remaining.', {
                variant: 'warning',
            });
            return;
        }

        const sanitizedData = {
            data: {
                group_resource_option_id: groupOption.id,
                room_party_members: roomPartyMembers.map((roomPartyMember) => roomPartyMember.id),
                action,
            }
        };

        sendUpdateRoomPartyMemberOptionBulk({ variables: { ...sanitizedData } })
            .then(() => {
                enqueueSnackbar('Options Updated.', {variant: 'success'});
            })
            .catch(() => {
                enqueueSnackbar('Error Updating Options.', {variant: 'error'});
            });
    }

    const setRoomPartyMemberOption = (roomMember: RoomPartyMember, groupOption: GroupResourceOption, newValue: number, remaining: number, memberOption?: RoomPartyMemberOption) => {
        if (newValue >= 0) {

            const currentCount = memberOption ? memberOption.unit_count : 0;
            const maxAllowed = currentCount + remaining;

            if (newValue <= maxAllowed || remaining < 0) {
                if (memberOption) {
                    const sanitisedData = {
                        data: {
                            id: memberOption.id,
                            room_party_member_id: roomMember!.id,
                            group_resource_option_id: groupOption.id,
                            unit_count: newValue,
                            unit_cost: memberOption.unit_cost!,
                            admin_edited: true,
                        }
                    };
                    sendUpdateRoomPartyMemberOption({variables: {...sanitisedData}})
                        .then(() => {
                            enqueueSnackbar('Option Updated.', {variant: 'success'});
                        })
                        .catch(() => {
                            enqueueSnackbar('Option Update Failed!', {variant: 'error'});
                        });
                } else {
                    const sanitisedData = {
                        data: {
                            room_party_member_id: roomMember!.id,
                            group_resource_option_id: groupOption.id,
                            unit_count: newValue,
                            unit_cost: groupOption.unit_cost!,
                            admin_edited: true,
                        }
                    };
                    sendCreateRoomPartyMemberOption({variables: sanitisedData})
                        .then(() => {
                            enqueueSnackbar('Option Created.', {variant: 'success'});
                        })
                        .catch(() => {
                            enqueueSnackbar('Create Option Failed!', {variant: 'error'});
                        });
                }
            } else {
                enqueueSnackbar('Value exceeds remaining', {variant: 'error'});
            }
        } else {
            if (memberOption) {
                sendDeleteRoomPartyMemberOption({variables: {id: memberOption.id}})
                    .then(() => {
                        enqueueSnackbar('Option Deleted.', {variant: 'success'});
                    })
                    .catch(() => {
                        enqueueSnackbar('Delete Option Failed!', {variant: 'error'});
                    });
            }
        }
    };

    const checkLevel = checkEditLevel();
    const showPrices = isAdmin || (moment(group.end_date).add(15, 'days').diff(moment(), 'days') > 0);
    const deletedOptions: any[] = []
    roomPartyMembers.map(roomPartMember => {
        deletedOptions.push(...roomPartMember.options!.filter(option => option.deleted_at !== null))
    })
    return (
        <Card className={classes.container}>
            <LoadingOverlay loading={loadingData.loading}>
                <CardHeader title="Room Guest Options"/>
                <CardContent className={classes.card}>

                    <SearchBar onSearch={(value: string) => setOptionsFilter(value)}/><br/>

                    {
                        deletedOptions.length > 0 && (
                            <Typography variant="h5">These options could not be restored:</Typography>
                        )
                    }
                    {
                        deletedOptions && deletedOptions.map(deleteOption => {
                            const groupOption = group && group.options && group.options.find(option => deleteOption.group_resource_option_id! === option.id);
                            if (!groupOption) return '';
                            return (
                                <Typography variant="body1">{groupOption.option.internal_name}</Typography>
                            );
                        })
                    }
                    {showScrollers && (
                        <Grid container>
                            <Grid item xs={12} className={classes.tableButtonsRow}>
                                <IconButton color="secondary" onClick={handleLeftClick}>
                                    <ArrowRight/>
                                </IconButton>
                                <IconButton color="secondary" onClick={handleRightClick}>
                                    <ArrowLeft/>
                                </IconButton>
                            </Grid>
                        </Grid>
                    )}

                    <div style={{overflow: 'auto', width: '100%', position: 'relative'}} ref={tableScrollRef}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Option Name</TableCell>
                                    <TableCell>Available</TableCell>
                                    {showPrices && (<TableCell>Option Cost</TableCell>)}
                                    {roomPartyMembers.length > 1 && (
                                        <TableCell style={{width: 80}}>Update All</TableCell>
                                    )}
                                    {roomPartyMembers.map(guestData =>
                                        (<TableCell key={guestData.id}
                                                    style={{width: 80}}>{guestData.groupMember!.first_name} {guestData.groupMember!.last_name.substr(0, 1)}</TableCell>)
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {group && group.options && group.options
                                    .filter(groupOption =>
                                        groupOption.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER ||
                                        groupOption.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER_PER_NIGHT ||
                                        groupOption.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_AVAILABLE ||
                                        groupOption.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_MANDATORY
                                    )
                                    .filter(groupOption => groupOption.option!.internal_name!.indexOf(optionsFilter) !== -1)
                                    .filter(groupOption => isAdmin || checkLevel(group.id, groupOption.view_level!))
                                    .map(groupOption => {
                                        const maxGuestValue = roomPartyMembers.reduce((value, roomMember) => {
                                            const memberOption = roomMember.options && roomMember.options.find(roomMemberOption => roomMemberOption.group_resource_option_id! === groupOption.id && roomMemberOption.deleted_at === null);
                                            const memberValue = memberOption && memberOption.unit_count > 0 ? memberOption.unit_count : 0;
                                            return memberValue > value ? memberValue : value;
                                        }, 0);
                                        const currentResource = groupOption.option.resource!;
                                        const remaining = currentResource.consumable && !currentResource.allow_manual_overbooking ? currentResource.resource_banks![0].remaining! : 999999;
                                        const isAvailable = remaining > 0;
                                        const isMandatory = groupOption.action_type === ResourceOptionAction.TRAVELLER_RESOURCE_MANDATORY ||
                                            groupOption.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER ||
                                            groupOption.action_type === ResourceOptionAction.ADD_COST_TO_TRAVELLER_PER_NIGHT;
                                        const isAdminOnly = groupOption.option && groupOption.option.is_admin_only
                                        const canEditOption = isAdmin || checkLevel(group.id, groupOption.editing_level!);

                                        return (
                                            <TableRow
                                                hover
                                                key={groupOption.id}
                                            >
                                                <TableCell>
                                                    {groupOption.option.internal_name}

                                                    {isMandatory && (
                                                        <Chip icon={<ErrorOutlineIcon/>} label="rules based mandatory"
                                                              className={classes.chipMandatory} size="small"/>
                                                    )}
                                                    {isAdminOnly && (
                                                        <Chip icon={<ErrorOutlineIcon/>} label="admin only"
                                                              className={classes.chipAdminOnly} size="small"/>
                                                    )}
                                                </TableCell>
                                                <TableCell>{remaining < 99999 ? remaining : 'yes'}</TableCell>
                                                {showPrices && (<TableCell>${groupOption.unit_cost}</TableCell>)}
                                                {roomPartyMembers.length > 1 && (
                                                    <TableCell>
                                                        <NumberSpinner
                                                            value={maxGuestValue}
                                                            min={0}
                                                            max={Math.min(Math.floor(remaining / roomPartyMembers.length), remaining)}
                                                            onChange={(newValue) => {
                                                                // do nothing
                                                            }}
                                                            decrement={() => setAllRoomPartyMemberOption(groupOption, 'decrement', Math.min(Math.floor(remaining / roomPartyMembers.length), remaining))}
                                                            increment={() => setAllRoomPartyMemberOption(groupOption, 'increment', Math.min(Math.floor(remaining / roomPartyMembers.length), remaining))}
                                                            disabled={(!isAvailable && maxGuestValue === 0) || !canEditOption}
                                                            useInput={false}
                                                        />
                                                    </TableCell>
                                                )}
                                                {roomPartyMembers.map(guestData => {
                                                        const memberOption = guestData.options && guestData.options.find(roomMemberOption => roomMemberOption.group_resource_option_id! === groupOption.id);
                                                        const optionUnavailable = memberOption && memberOption.deleted_at === null
                                                        const value = memberOption && (memberOption.unit_count > 0 && memberOption.deleted_at === null) ? memberOption.unit_count : 0;
                                                        const effectiveMax = remaining < 0 ? Infinity : remaining + value;

                                                        return (
                                                            <TableCell key={guestData.id}>
                                                                <NumberSpinner
                                                                    value={value}
                                                                    min={0} max={effectiveMax}
                                                                    disabled={(!isAvailable && value === 0) || !canEditOption}
                                                                    onChange={(newValue) => setRoomPartyMemberOption(guestData, groupOption, newValue, remaining, memberOption)}
                                                                />
                                                                {memberOption && memberOption.admin_edited && (
                                                                    <React.Fragment>
                                                                        <br/>
                                                                        <Chip icon={<ErrorOutlineIcon/>}
                                                                              label="admin edited"
                                                                              className={classes.chipAdminEdited}
                                                                              size="small"/>
                                                                    </React.Fragment>
                                                                )}
                                                            </TableCell>
                                                        );
                                                    }
                                                    )}
                                                </TableRow>
                                            )
                                        }
                                    )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </LoadingOverlay>
        </Card>
    );
};

export const RoomPartyMemberOptionsEdit = (props: { groupId: number, roomPartyMembers: RoomPartyMember[], isAdmin: boolean, canEdit: boolean, groupRef?: string }) => {
    const {isAdmin, canEdit, groupRef, groupId, roomPartyMembers} = props;
    const groupData = isAdmin ? getGroupWithQuery(groupId, gqlGroupRoomPartyOptions) : getGroupByRef(groupRef!);
    const group = isAdmin ? groupData.data && groupData.data.group : groupData.data && groupData.data.groupByRef;
    const {enqueueSnackbar} = useSnackbar();

    useEffect(() => {
        if (groupData.error) {
            enqueueSnackbar(`There was an error loading Group Data`, {variant: 'error'});
        }
    }, [groupData.error]);

    if (groupData.error) return (<div>There was an error loading group data.</div>);

    return (
        <React.Fragment>
            <LoadingOverlay loading={groupData.loading}>
                {group && (
                    <RoomPartyMemberOptionsEditContent group={group} roomPartyMembers={roomPartyMembers} isAdmin={isAdmin} canEdit={canEdit}/>)}
            </LoadingOverlay>
        </React.Fragment>
    );
};

export default RoomPartyMemberOptionsEdit;
